import { Box, Typography, Stack, Select, MenuItem, IconButton, Chip, Paper } from '@mui/material';
import { Close } from '@mui/icons-material';
import ValueInput from './ValueInput';
import { OPERATORS_BY_TYPE, OPERATOR_LABELS, NO_VALUE_OPS, defaultOperator } from './constants';

/* Top-level sub-components so their type is stable across FilterRow re-renders */

const FieldSelect = ({ value, schema, fieldType, onChange, fullWidth }) => (
  <Select
    size="small"
    value={value}
    onChange={e => onChange(e.target.value)}
    displayEmpty
    fullWidth={fullWidth}
    renderValue={v => v
      ? <Box>
          <Typography variant="body2" component="span">{v}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>({fieldType})</Typography>
        </Box>
      : <Typography variant="body2" color="text.disabled">Select field…</Typography>
    }
    sx={{ minWidth: fullWidth ? undefined : 180 }}
  >
    {schema.map(f => (
      <MenuItem key={f.key} value={f.key}>
        <Box>
          <Typography variant="body2">{f.key}</Typography>
          <Typography variant="caption" color="text.secondary">{f.type}</Typography>
        </Box>
      </MenuItem>
    ))}
  </Select>
);

const OperatorSelect = ({ value, ops, onChange, disabled, fullWidth }) => (
  <Select
    size="small"
    value={value}
    onChange={e => onChange(e.target.value)}
    disabled={disabled}
    fullWidth={fullWidth}
    sx={{ minWidth: fullWidth ? undefined : 140 }}
  >
    {ops.map(op => (
      <MenuItem key={op} value={op}>{OPERATOR_LABELS[op]}</MenuItem>
    ))}
  </Select>
);

const FilterRow = ({ filter, schema, onChange, onRemove, index, isMobile }) => {
  const ops      = OPERATORS_BY_TYPE[filter.fieldType] ?? OPERATORS_BY_TYPE.string;
  const noValue  = NO_VALUE_OPS.has(filter.operator);
  const isBetween = filter.operator === 'between';

  const handleFieldChange = (newField) => {
    const newType = schema.find(f => f.key === newField)?.type ?? 'string';
    onChange({ ...filter, field: newField, fieldType: newType, operator: defaultOperator(newType), value: '', valueTo: '' });
  };

  const handleOperatorChange = (op) => onChange({ ...filter, operator: op, value: '', valueTo: '' });

  const sharedFieldProps = {
    value: filter.field,
    schema,
    fieldType: filter.fieldType,
    onChange: handleFieldChange,
  };

  const sharedOpProps = {
    value: filter.operator,
    ops,
    onChange: handleOperatorChange,
    disabled: !filter.field,
  };

  /* Mobile: card per filter, vertical stacking */
  if (isMobile) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Chip
            label={`Filter ${index + 1}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: 11, height: 20 }}
          />
          <IconButton size="small" color="error" onClick={onRemove}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Stack spacing={1}>
          <FieldSelect {...sharedFieldProps} fullWidth />
          <OperatorSelect {...sharedOpProps} fullWidth />
          {!noValue && (
            <ValueInput
              fieldType={filter.fieldType}
              val={filter.value}
              onChange={v => onChange({ ...filter, value: v })}
              placeholder="value"
              fullWidth
            />
          )}
          {isBetween && (
            <>
              {!isMobile && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>to</Typography>
              )}
              <ValueInput
                fieldType={filter.fieldType}
                val={filter.valueTo}
                onChange={v => onChange({ ...filter, valueTo: v })}
                placeholder="to"
                fullWidth
              />
            </>
          )}
        </Stack>
      </Paper>
    );
  }

  /* Desktop: inline row */
  return (
    <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" py={0.5}>
      <FieldSelect {...sharedFieldProps} />
      <OperatorSelect {...sharedOpProps} />
      {!noValue && (
        <ValueInput
          fieldType={filter.fieldType}
          val={filter.value}
          onChange={v => onChange({ ...filter, value: v })}
          placeholder="value"
        />
      )}
      {isBetween && (
        <>
          <Typography variant="caption" color="text.secondary">to</Typography>
          <ValueInput
            fieldType={filter.fieldType}
            val={filter.valueTo}
            onChange={v => onChange({ ...filter, valueTo: v })}
            placeholder="to"
          />
        </>
      )}
      <IconButton size="small" color="error" onClick={onRemove}>
        <Close fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default FilterRow;
